import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react"
import { Button, Container, Stack, Typography } from "@mui/material"
import { useLocation } from "react-router-dom"
import { Bound } from "lib/@components/binding/Bound"
import { useRefresh } from "lib/@hooks/useRefresh"
import { Case, CaseElse, If, Switch } from "lib/@components/switch"
import { Else } from "lib/@components/else"
import { useSearchParam, useSearchParamsBatch } from "lib/@hooks/use-search-param"
import { confirm } from "lib/@dialogs/confirm"
import { hasDemand } from "lib/authorization/has-demand"
import DemandGuard from "minimals-template/components/guards/DemandGuard"
import {
    AUTHORING_MANAGE_MAINTENANCE_LIBRARY_DEMAND,
    AUTHORING_REVIEW_COMPONENT_DEMAND,
} from "authoring/lib/constants/authoring-permissions.mjs"
import {
    deleteMaintenanceCategory,
    deleteMaintenanceTask,
    getHistoricalMaintenanceCategory,
    getHistoricalMaintenanceTask,
    getMaintenanceLibraryCheckpoints,
    getMaintenanceCategory,
    getMaintenanceDirectories,
    getMaintenanceTask,
} from "../controller/maintenance-library"
import { CategoryList } from "./components/category-list"
import { TaskView } from "./components/task-view"
import { CategoryEditor } from "./components/category-editor"
import { UploadLibraryDialog } from "./components/upload-library-dialog"
import { SourceEditorDialog } from "./components/source-editor-dialog"
import { OriginalSourcePanel } from "./components/original-source-panel"
import { SearchResults } from "./components/search-results"
import { UnmappedDocumentsList } from "./components/unmapped-documents-list"
import { LibraryHeader } from "./components/library-header"
import { LibrarySearch } from "./components/library-search"
import { HistoricalLibraryToolbar } from "./components/historical-library-toolbar"
import { DraftComponentsList } from "./components/draft-components-list"
import { DraftReviewBanner } from "./components/draft-review-banner"
import {
    MODE_BROWSE,
    MODE_DRAFTS,
    MODE_UNMAPPED,
    VIEW_DRAFTS,
    VIEW_LIST,
    VIEW_TASK,
    VIEW_UNMAPPED,
} from "./lib/library-views"
import { buildLibraryBreadcrumbs } from "./lib/build-library-breadcrumbs"

const EMPTY_LIST = []
const scrollPositions = {}

export function MaintenanceLibraryExplorer() {
    const [categoryId] = useSearchParam("libraryCategory", "components")
    const [selectedTaskId] = useSearchParam("libraryTask", "")
    const [search] = useSearchParam("search", "")
    const [libraryMode] = useSearchParam("libraryMode", MODE_BROWSE)
    const [libraryView] = useSearchParam("libraryView", "")
    const [libraryAsOf] = useSearchParam("libraryAsOf", "")
    const [libraryTaskVersion] = useSearchParam("libraryTaskVersion", "")
    const updateParams = useSearchParamsBatch()
    const location = useLocation()
    const historicalMode = Boolean(libraryAsOf)
    const canEdit = hasDemand(AUTHORING_MANAGE_MAINTENANCE_LIBRARY_DEMAND)
    const readOnly = historicalMode || Boolean(libraryTaskVersion) || !canEdit

    const setCategoryId = useCallback(
        (id) =>
            updateParams(
                {
                    libraryCategory: id,
                    libraryTask: "",
                    libraryTaskVersion: "",
                    libraryMode: MODE_BROWSE,
                    libraryView: VIEW_LIST,
                    libraryFrom: "",
                },
                { push: true }
            ),
        [updateParams]
    )
    const setSelectedTaskId = useCallback(
        // `from` records which view the task was opened from, so the toolbar can offer the
        // matching way back. Defaults to "", which clears the param for every other caller.
        (id, { from = "" } = {}) =>
            updateParams(
                {
                    libraryTask: id || "",
                    libraryTaskVersion: "",
                    libraryMode: MODE_BROWSE,
                    libraryView: id ? VIEW_TASK : VIEW_LIST,
                    libraryFrom: id ? from : "",
                },
                { push: true }
            ),
        [updateParams]
    )
    const setLibraryTaskVersion = useCallback(
        (version) =>
            updateParams(
                {
                    libraryTaskVersion: version ? String(version) : "",
                    libraryView: selectedTaskId ? VIEW_TASK : VIEW_LIST,
                },
                { push: true }
            ),
        [selectedTaskId, updateParams]
    )
    const setLibraryAsOf = useCallback(
        (value, options = {}) =>
            updateParams(
                {
                    libraryAsOf: value || "",
                    libraryTaskVersion: options.preserveTaskVersion ? libraryTaskVersion : "",
                    libraryMode: MODE_BROWSE,
                    libraryView: selectedTaskId && selectedTaskId !== "new" ? VIEW_TASK : VIEW_LIST,
                    libraryTask: selectedTaskId === "new" ? "" : selectedTaskId,
                },
                { push: true }
            ),
        [libraryTaskVersion, selectedTaskId, updateParams]
    )

    const [addCategoryOpen, setAddCategoryOpen] = useState(false)
    const [addCategoryType, setAddCategoryType] = useState("directory")
    const [uploadOpen, setUploadOpen] = useState(false)
    const [sourceEditorOpen, setSourceEditorOpen] = useState(false)
    const refresh = useRefresh()
    const directoryResults = getMaintenanceDirectories.useResults()
    const directories = directoryResults ?? EMPTY_LIST
    const checkpointResults = getMaintenanceLibraryCheckpoints.useResults()
    const checkpoints = checkpointResults ?? EMPTY_LIST
    const baselineCheckpoint = useMemo(getBaselineCheckpoint, [checkpoints])
    const historicalSupportedFrom = baselineCheckpoint?.createdAt
    const unsupportedHistoricalView =
        historicalMode &&
        historicalSupportedFrom &&
        new Date(libraryAsOf).getTime() < new Date(historicalSupportedFrom).getTime()
    const currentCategory = getMaintenanceCategory.useResults(historicalMode ? undefined : categoryId)
    const historicalCategory = getHistoricalMaintenanceCategory.useResults(
        historicalMode && !unsupportedHistoricalView && categoryId ? { id: categoryId, asOf: libraryAsOf } : undefined
    )
    const category = historicalMode ? historicalCategory : currentCategory
    const currentTask = getMaintenanceTask.useResults(
        historicalMode ? undefined : selectedTaskId && selectedTaskId !== "new" ? selectedTaskId : undefined
    )
    const historicalTask = getHistoricalMaintenanceTask.useResults(
        historicalMode && !unsupportedHistoricalView && selectedTaskId && selectedTaskId !== "new"
            ? { id: selectedTaskId, asOf: libraryAsOf }
            : undefined
    )
    const task = historicalMode ? historicalTask : currentTask

    const activeView = useMemo(getActiveView, [historicalMode, libraryMode, libraryView])
    const isBrowseMode = activeView === VIEW_LIST
    const scrollRestorationKey = useMemo(getScrollRestorationKey, [location.pathname, location.search])
    const isFolder = !category || category.type === "directory" || categoryId === "components"

    useEffect(setupManualScrollRestoration, [])
    useLayoutEffect(restorePageScroll, [scrollRestorationKey])
    useEffect(trackPageScroll, [scrollRestorationKey])

    const breadcrumbs = useMemo(buildBreadcrumbs, [category, categoryId, directories, selectedTaskId, task])

    if (historicalMode && unsupportedHistoricalView) {
        return renderUnsupportedHistoricalView()
    }

    if (category === undefined && categoryId) return <Typography sx={{ p: 3 }}>Loading...</Typography>

    if (!category && categoryId) {
        if (historicalMode) {
            return (
                <Container maxWidth={false} sx={{ py: 4 }}>
                    <Typography variant="h5" sx={{ mb: 2 }}>
                        Maintenance Library
                    </Typography>
                    <HistoricalLibraryToolbar
                        baselineCreatedAt={historicalSupportedFrom}
                        checkpoints={checkpoints}
                        historicalMode={historicalMode}
                        libraryAsOf={libraryAsOf}
                        onClearHistoricalMode={handleClearHistoricalMode}
                        onSetLibraryAsOf={setLibraryAsOf}
                        unsupportedHistoricalView={false}
                    />
                    <Typography variant="subtitle1">
                        This category did not exist at the selected point in time.
                    </Typography>
                </Container>
            )
        }

        // Special case: if we're at the root "components" and it doesn't exist, show upload option
        if (categoryId === "components") {
            return (
                <Container maxWidth={false} sx={{ py: 4 }}>
                    <Typography variant="h5" sx={{ mb: 2 }}>
                        Maintenance Library
                    </Typography>
                    <If value={canEdit}>
                        <Typography variant="body1" sx={{ mb: 3 }}>
                            No library data found. Upload a zip file to get started.
                        </Typography>
                        <Button variant="contained" color="primary" onClick={() => setUploadOpen(true)}>
                            Upload Library Zip
                        </Button>
                        <UploadLibraryDialog
                            open={uploadOpen}
                            onClose={() => setUploadOpen(false)}
                            onSuccess={refresh}
                        />
                        <Else>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                                No library data found.
                            </Typography>
                        </Else>
                    </If>
                </Container>
            )
        }
        return (
            <Container maxWidth={false} sx={{ py: 4 }}>
                <Typography variant="subtitle1">Category not found: {categoryId}</Typography>
                <Button variant="contained" color="primary" sx={{ mt: 2 }} onClick={() => setCategoryId("components")}>
                    Go to Library Root
                </Button>
            </Container>
        )
    }

    return (
        <Bound
            category={category}
            categoryId={categoryId}
            task={task}
            onSelectView={handleSelectView}
            historicalMode={historicalMode}
            libraryAsOf={libraryAsOf}
            libraryTaskVersion={libraryTaskVersion}
            setLibraryAsOf={setLibraryAsOf}
            setLibraryTaskVersion={setLibraryTaskVersion}
            setCategoryId={setCategoryId}
            openCategoryFromUnmapped={openCategoryFromUnmapped}
            readOnly={readOnly}
            canEdit={canEdit}
            selectedTaskId={selectedTaskId}
            setSelectedTaskId={setSelectedTaskId}
            refresh={refresh}
            breadcrumbs={breadcrumbs}
            embedded={false}
            activeView={activeView}
            isBrowseMode={isBrowseMode}
            isFolder={isFolder}
            checkpoints={checkpoints}
            historicalSupportedFrom={historicalSupportedFrom}
            libraryActions={{
                onDeleteFolder: handleDeleteFolder,
                onUpload: handleUpload,
                onAddFolder: handleAddFolder,
                onAddTaskGroup: handleAddTaskGroup,
                onAddFile: handleAddFile,
                onAddTask: handleAddTask,
                onDeleteTask: handleDeleteTask,
                onSelectTask: setSelectedTaskId,
                onSelectView: handleSelectView,
                onClearHistoricalMode: handleClearHistoricalMode,
                onSetLibraryAsOf: setLibraryAsOf,
            }}
        >
            <Container maxWidth={false} sx={{ py: 4 }}>
                <Stack spacing={2} sx={{ mb: 3 }}>
                    <LibraryHeader />
                    <If value={!selectedTaskId && isBrowseMode}>
                        <LibrarySearch />
                    </If>
                </Stack>

                <If value={!selectedTaskId && isBrowseMode}>
                    <DraftReviewBanner />
                </If>

                <If value={selectedTaskId}>
                    <TaskView />

                    <Else>
                        <Switch value={activeView}>
                            <Case when={VIEW_UNMAPPED}>
                                <UnmappedDocumentsList />
                            </Case>
                            <Case when={VIEW_DRAFTS}>
                                {/* Gated on the demand alone, not the feature flag: the permission is
                                    permanent, the flag is temporary scaffolding. Scoped to this branch
                                    only — guarding the page would lock out authors who can manage the
                                    library but not review it. */}
                                <DemandGuard
                                    demands={AUTHORING_REVIEW_COMPONENT_DEMAND}
                                    redirect={false}
                                    message="You do not have permission to review draft components."
                                >
                                    <DraftComponentsList />
                                </DemandGuard>
                            </Case>
                            <CaseElse>
                                <If value={search}>
                                    <SearchResults search={search} categoryId={categoryId} />

                                    <Else>
                                        <CategoryList />
                                        <OriginalSourcePanel onEditSource={handleEditSource} />
                                    </Else>
                                </If>
                            </CaseElse>
                        </Switch>
                    </Else>
                </If>

                <CategoryEditor
                    open={addCategoryOpen}
                    onClose={() => setAddCategoryOpen(false)}
                    parentId={category?.id}
                    type={addCategoryType}
                />

                <UploadLibraryDialog open={uploadOpen} onClose={() => setUploadOpen(false)} />

                <SourceEditorDialog
                    open={sourceEditorOpen}
                    onClose={() => setSourceEditorOpen(false)}
                    category={category}
                />
            </Container>
        </Bound>
    )

    function handleSelectView(view) {
        if (view === VIEW_LIST) {
            updateParams({ libraryMode: MODE_BROWSE, libraryView: VIEW_LIST, libraryFrom: "" }, { push: true })
            return
        }

        // Unmapped and drafts are both library-wide views, so reset the folder, any selected
        // task and the search box before switching into them.
        updateParams(
            {
                libraryMode: view === VIEW_DRAFTS ? MODE_DRAFTS : MODE_UNMAPPED,
                libraryView: view,
                libraryCategory: "components",
                libraryTask: "",
                libraryTaskVersion: "",
                libraryFrom: "",
                search: "",
            },
            { push: true }
        )
    }

    function openCategoryFromUnmapped(id) {
        updateParams(
            {
                libraryMode: MODE_BROWSE,
                libraryView: VIEW_LIST,
                libraryCategory: id,
                libraryTask: "",
                libraryTaskVersion: "",
                search: "",
            },
            { push: true }
        )
    }

    function handleAddFolder() {
        setAddCategoryType("directory")
        setAddCategoryOpen(true)
    }

    function handleAddTaskGroup() {
        setAddCategoryType("taskGroup")
        setAddCategoryOpen(true)
    }

    function handleAddFile() {
        setAddCategoryType("file")
        setAddCategoryOpen(true)
    }

    async function handleDeleteFolder() {
        const type = category?.type === "file" ? "file" : "folder"
        if (await confirm(`Are you sure you want to delete the ${type} "${category?.name}" and all its contents?`)) {
            const parentId = category?.parentId || "components"
            await deleteMaintenanceCategory(categoryId)
            setCategoryId(parentId)
            refresh()
        }
    }

    async function handleDeleteTask() {
        if (await confirm("Are you sure you want to delete this component?")) {
            await deleteMaintenanceTask(selectedTaskId)
            setSelectedTaskId(null)
            refresh()
        }
    }

    function handleEditSource() {
        setSourceEditorOpen(true)
    }

    function handleClearHistoricalMode() {
        updateParams(
            {
                libraryAsOf: "",
                libraryTaskVersion: "",
                libraryMode: MODE_BROWSE,
                libraryView: selectedTaskId ? VIEW_TASK : VIEW_LIST,
            },
            { push: true }
        )
    }

    function getActiveView() {
        if (historicalMode) return VIEW_LIST
        if (libraryView === VIEW_UNMAPPED) return VIEW_UNMAPPED
        if (libraryView === VIEW_DRAFTS) return VIEW_DRAFTS
        if (libraryView === VIEW_LIST || libraryView === VIEW_TASK) return VIEW_LIST
        if (libraryMode === MODE_UNMAPPED) return VIEW_UNMAPPED
        if (libraryMode === MODE_DRAFTS) return VIEW_DRAFTS
        return VIEW_LIST
    }

    function getBaselineCheckpoint() {
        if (!checkpoints.length) return null
        return [...checkpoints].sort((left, right) => new Date(left.createdAt) - new Date(right.createdAt))[0]
    }

    function renderUnsupportedHistoricalView() {
        return (
            <Container maxWidth={false} sx={{ py: 4 }}>
                <Typography variant="h5" sx={{ mb: 2 }}>
                    Maintenance Library
                </Typography>
                <HistoricalLibraryToolbar
                    baselineCreatedAt={historicalSupportedFrom}
                    checkpoints={checkpoints}
                    historicalMode={historicalMode}
                    libraryAsOf={libraryAsOf}
                    onClearHistoricalMode={handleClearHistoricalMode}
                    onSetLibraryAsOf={setLibraryAsOf}
                    unsupportedHistoricalView={true}
                />
                <Typography variant="body1" color="text.secondary">
                    Choose a time on or after the baseline checkpoint to browse library history.
                </Typography>
            </Container>
        )
    }

    function buildBreadcrumbs() {
        return buildLibraryBreadcrumbs({ category, categoryId, directories, selectedTaskId, task })
    }

    function handleUpload() {
        setUploadOpen(true)
    }

    function handleAddTask() {
        setSelectedTaskId("new")
    }

    function setupManualScrollRestoration() {
        const previous = window.history.scrollRestoration
        window.history.scrollRestoration = "manual"
        return () => {
            window.history.scrollRestoration = previous
        }
    }

    function getScrollRestorationKey() {
        return `${location.pathname}${location.search}`
    }

    function restorePageScroll() {
        const top = scrollPositions[scrollRestorationKey] ?? 0
        window.requestAnimationFrame(() => {
            window.scrollTo(0, top)
        })
    }

    function trackPageScroll() {
        window.addEventListener("scroll", handleScroll, { passive: true })
        return () => {
            handleScroll()
            window.removeEventListener("scroll", handleScroll)
        }

        function handleScroll() {
            scrollPositions[scrollRestorationKey] = window.scrollY
        }
    }
}

export default MaintenanceLibraryExplorer
